import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { lsGet } from '../lib/storage';
import { useWallet } from '../hooks/use-wallet';
import { userWriteHeaders } from '../lib/api';

const MODELS = [
  { id: 'gemini',        label: 'Gemini',        desc: 'Fast · ~1p per bayt',    estimatePence: 1 },
  { id: 'claude_sonnet', label: 'Claude Sonnet',  desc: 'Deep · ~3p per bayt',    estimatePence: 3 },
  { id: 'openai',        label: 'GPT-4o',         desc: 'Precise · ~4p per bayt', estimatePence: 4 },
];

const SCOPES = [
  { id: 'bayt', label: 'This bayt', desc: 'Single verse analysis' },
  { id: 'poem', label: 'Full poem', desc: 'Whole poem analysis'   },
];

export default function AnalysisModal({ poemId, baytId, verseAr, poemTitleAr, poetAr, era, verses, onClose, onResult, onTopUp }) {
  const navigate = useNavigate();
  const userId = lsGet('diwanPrismaUserId');
  const { balance, loading: balanceLoading, refresh: refreshWallet } = useWallet(userId);
  const idempotencyKey = useRef(`${userId ?? 'anon'}-${poemId ?? ''}-${baytId ?? ''}-${crypto.randomUUID()}`);

  const [model, setModel] = useState('gemini');
  const [scope, setScope] = useState(baytId ? 'bayt' : 'poem');
  const [status, setStatus] = useState('idle'); // idle | submitting | polling | done | error
  const [errorMsg, setErrorMsg] = useState('');

  const selectedModel = MODELS.find(m => m.id === model);
  const estimatePence = selectedModel?.estimatePence ?? 1;
  // Guests (no userId) never have balance. Signed-in: optimistic while loading, then check.
  const hasBalance = userId ? (balanceLoading || balance === null || balance >= estimatePence) : false;
  const busy = status === 'submitting' || status === 'polling';

  const poll = useCallback(async (analysisId) => {
    const maxAttempts = 30;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 2000));
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`/api/analyse?id=${analysisId}`, {
        headers: userWriteHeaders(userId, session?.access_token),
      });
      if (!res.ok) continue;
      const data = await res.json();
      if (data.status === 'completed' && data.result) return data.result;
      if (data.status === 'failed') throw new Error('Analysis failed — your balance has been refunded.');
    }
    throw new Error('Analysis timed out. Please try again.');
  }, [userId]);

  function handleRetry() {
    // Generate a fresh idempotency key so the retry is treated as a new job
    idempotencyKey.current = `${userId ?? 'anon'}-${poemId ?? ''}-${baytId ?? ''}-${crypto.randomUUID()}`;
    setStatus('idle');
    setErrorMsg('');
  }

  async function handleSubmit() {
    setStatus('submitting');
    setErrorMsg('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) { setErrorMsg('Not signed in'); setStatus('error'); return; }

      const res = await fetch('/api/analyse', {
        method: 'POST',
        headers: userWriteHeaders(userId, token),
        body: JSON.stringify({
          scope, model, poemId, baytId, idempotencyKey: idempotencyKey.current,
          verseAr, poemTitleAr, poetAr, era, verses,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        if (res.status === 402) {
          setErrorMsg(`Insufficient balance — you need at least ${estimatePence}p.`);
          setStatus('error');
          return;
        }
        throw new Error(data.error ?? 'Analysis request failed');
      }

      if (data.cached) {
        setStatus('done');
        onResult?.(data.result);
        return;
      }

      setStatus('polling');
      const analysisResult = await poll(data.analysisId);
      setStatus('done');
      refreshWallet();
      onResult?.(analysisResult);
    } catch (err) {
      setErrorMsg(err.message);
      setStatus('error');
    }
  }

  return (
    <div className="bottom-sheet-overlay" onClick={onClose}>
      <div className="bottom-sheet" onClick={e => e.stopPropagation()}>
        <div className="bottom-sheet__header">
          <h2 className="bottom-sheet__title">Fresh analysis</h2>
          <button className="bottom-sheet__close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {status === 'done' ? (
          <div className="analysis-modal__done">
            <p>Analysis complete. Results loaded.</p>
            <button className="analysis-modal__close-btn" onClick={onClose}>Close</button>
          </div>

        ) : status === 'polling' ? (
          <div className="analysis-modal__polling">
            <p>Analysing… this takes ~10 seconds.</p>
            <div className="analysis-modal__spinner" aria-label="Loading" />
          </div>

        ) : !userId ? (
          <div className="analysis-modal__signin-wall">
            <p className="analysis-modal__signin-reason">
              AI analysis uses your wallet credit — you need an account to run it.
            </p>
            <p className="analysis-modal__signin-hint">
              Once analysed, this verse is cached free for everyone.
            </p>
            <button
              className="analysis-modal__submit"
              onClick={() => { onClose(); navigate('/splash'); }}
            >
              Sign in to continue →
            </button>
          </div>

        ) : (
          <>
            <div className="analysis-modal__section">
              <p className="analysis-modal__label">Scope</p>
              <div className="analysis-modal__options">
                {SCOPES.filter(s => baytId ? true : s.id === 'poem').map(s => (
                  <button
                    key={s.id}
                    type="button"
                    className={`analysis-modal__option${scope === s.id ? ' active' : ''}`}
                    onClick={() => !busy && setScope(s.id)}
                    disabled={busy}
                  >
                    <span>{s.label}</span>
                    <small>{s.desc}</small>
                  </button>
                ))}
              </div>
            </div>

            <div className="analysis-modal__section">
              <p className="analysis-modal__label">Model</p>
              <div className="analysis-modal__options">
                {MODELS.map(m => (
                  <button
                    key={m.id}
                    type="button"
                    className={`analysis-modal__option${model === m.id ? ' active' : ''}`}
                    onClick={() => !busy && setModel(m.id)}
                    disabled={busy}
                  >
                    <span>{m.label}</span>
                    <small>{m.desc}</small>
                  </button>
                ))}
              </div>
            </div>

            <p className="analysis-modal__balance">
              {balanceLoading
                ? 'Balance: loading…'
                : `Balance: £${((balance ?? 0) / 100).toFixed(2)} · Est. cost: ~${estimatePence}p`}
            </p>

            {errorMsg && (
              <div className="analysis-modal__error-block">
                <p className="analysis-modal__error">{errorMsg}</p>
                {status === 'error' && errorMsg.includes('balance') && onTopUp && (
                  <button className="analysis-modal__topup-link" onClick={onTopUp}>
                    Top up wallet →
                  </button>
                )}
                {status === 'error' && !errorMsg.includes('balance') && (
                  <button className="analysis-modal__retry-btn" onClick={handleRetry}>
                    Try again →
                  </button>
                )}
              </div>
            )}

            {!errorMsg && (
              <>
                <button
                  className="analysis-modal__submit"
                  onClick={handleSubmit}
                  disabled={busy || !hasBalance}
                >
                  {status === 'submitting' ? 'Requesting…' : `Run analysis · ~${estimatePence}p`}
                </button>

                {!balanceLoading && !hasBalance && (
                  <p className="analysis-modal__topup-hint">
                    Insufficient balance —{' '}
                    {onTopUp
                      ? <button className="analysis-modal__topup-link" onClick={onTopUp}>top up your wallet</button>
                      : 'top up your wallet'
                    }
                    {' '}first.
                  </p>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
